import React, { useContext, useEffect, useState } from 'react'
import { Form, Input, Select, Typography } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { HalfWidthContainer, SpacedRows } from 'components/Assessments/components/styles'
import AssessmentFormContext from 'contexts/AssessmentFormContext'
import PatientContext from 'contexts/PatientContext'
import { useGenerateAnswerFromSectionResults } from '../../../hooks/useGenerateAnswerFromSectionResults'
import { SectionDependentItems } from '../../../types/questionnaire'
import { PatientBackgroundFormField } from '../../PatientBackground/formField'
import { UNANSWERED_SUMMARY_INPUT_DEFAULT } from '../../../constants'

const { Title, Text } = Typography

dayjs.extend(relativeTime)

export const AssessmentSummary: React.FC = () => {
  const { form } = useContext(AssessmentFormContext)
  const { displayName } = useContext(PatientContext)
  const [skilledTaskValue, setSkilledTaskValue] = useState<string[]>([])
  const [diagnosesValue, setDiagnosesValue] = useState<string[]>([])

  // Get patient verification data from form state
  const legalName = Form.useWatch(PatientBackgroundFormField.LEGAL_NAME, form) as string | undefined
  const preferredName = Form.useWatch(PatientBackgroundFormField.PREFERRED_NAME, form) as
    | string
    | undefined
  const dateOfBirth = Form.useWatch(PatientBackgroundFormField.DATE_OF_BIRTH, form) as
    | Dayjs
    | undefined

  const {
    generatedItemValue: skilledTasksDisplayValue,
    isSectionForItemComplete: isCompleteCNASection
  } = useGenerateAnswerFromSectionResults({
    questionLinkId: SectionDependentItems.SKILLED_NEEDS_LIST
  })
  const { generatedItemValue: diagnosesDisplayValue } = useGenerateAnswerFromSectionResults({
    questionLinkId: SectionDependentItems.DIAGNOSES_LIST
  })

  useEffect(() => {
    if (skilledTasksDisplayValue && Array.isArray(skilledTasksDisplayValue)) {
      setSkilledTaskValue(skilledTasksDisplayValue)
    }
    if (diagnosesDisplayValue && Array.isArray(diagnosesDisplayValue)) {
      setDiagnosesValue(diagnosesDisplayValue)
    }
  }, [skilledTasksDisplayValue, diagnosesDisplayValue])

  const patientName = legalName || preferredName || displayName || ''
  const age = dateOfBirth ? dayjs().from(dateOfBirth, true) : 'None'

  return (
    <>
      <Title level={3}>Assessment Summary</Title>
      <Text type="secondary">{`Based on the information you entered, here is a summary of ${displayName}`}</Text>
      <div style={{ padding: '20px 0' }}>
        <SpacedRows>
          <HalfWidthContainer>Patient Name</HalfWidthContainer>
          <HalfWidthContainer>
            <Input value={patientName} disabled />
          </HalfWidthContainer>
        </SpacedRows>
        <SpacedRows>
          <HalfWidthContainer>Age</HalfWidthContainer>
          <HalfWidthContainer>
            <Input value={age} disabled />
          </HalfWidthContainer>
        </SpacedRows>
        <SpacedRows>
          <HalfWidthContainer>Diagnoses</HalfWidthContainer>
          <HalfWidthContainer>
            <Select
              mode="multiple"
              value={diagnosesValue}
              disabled
              suffixIcon={null}
              removeIcon={null}
              notFoundContent={null}
              showSearch={false}
              placeholder={UNANSWERED_SUMMARY_INPUT_DEFAULT}
              style={{ width: '100%' }}
            />
          </HalfWidthContainer>
        </SpacedRows>
        <SpacedRows>
          <HalfWidthContainer>Skilled Assistance Needs</HalfWidthContainer>
          <HalfWidthContainer>
            <Select
              mode="multiple"
              value={skilledTaskValue}
              disabled
              suffixIcon={null}
              removeIcon={null}
              notFoundContent={null}
              showSearch={false}
              placeholder={
                isCompleteCNASection === false ? UNANSWERED_SUMMARY_INPUT_DEFAULT : undefined
              }
              style={{ width: '100%' }}
            />
          </HalfWidthContainer>
        </SpacedRows>
      </div>
    </>
  )
}
